/**
 * תצוגת סכום בכרטיס המחיר בעמוד success ייעודי של `/packages/profile/:id/success` בלבד.
 * משתמש רק בשדות שכבר הגיעו מהשרת/order — לא מחשב מחיר מחדש.
 */

function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return NaN;
  return Math.round(x * 100) / 100;
}

function parsePositiveMoney(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return round2(num);
}

/**
 * סיכום מאומת של תוספות מסוג supplement עם checked ועם fare (לביקורת / SSOT תצוגתי secondary).
 */
export function sumCheckedSupplementFareIls(order) {
  const raw = order?.supplement ?? order?.supplements;
  if (!raw || typeof raw !== "object") return 0;
  const rows = Array.isArray(raw) ? raw : Object.values(raw);
  let sum = 0;
  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const checked =
      row.checked === true ||
      row.checked === 1 ||
      String(row.checked || "").trim() === "1";
    const fare = Number(row.ourFare ?? row.fare ?? row.amount ?? row.price ?? 0);
    if (checked && Number.isFinite(fare) && fare > 0) sum += fare;
  });
  return round2(sum);
}

const ADDITIVE_BAGGAGE_WHEN_MISSING_FULL_TOTAL = new Set([
  "priceWithCreditCardFee",
  "price_with_credit_card_fee",
  "priceWithHandleFee",
  "price_with_handle_fee",
]);

const MONEY_TOLERANCE = 1.49;

/**
 * @returns {{
 *   ils: number|null,
 *   audit: Record<string, unknown>,
 * }}
 */
export function computePublicPackagesProfileSuccessPayableTotal({
  dedicatedSuccessRoute,
  order,
  persistedDisplay,
  baggageDetails,
}) {
  const baseTotalRaw = persistedDisplay?.ils ?? null;
  const baseTotal =
    baseTotalRaw != null && Number.isFinite(Number(baseTotalRaw)) ? Number(baseTotalRaw) : null;

  const supplementsTotal = order ? sumCheckedSupplementFareIls(order) : 0;

  const auditSkeleton = () => ({
    baseTotal,
    baggageTotal: 0,
    supplementsTotal,
    displayedTotal: baseTotal,
    payableTotal: baseTotal,
    source: persistedDisplay?.source ?? null,
  });

  if (!dedicatedSuccessRoute || order == null) {
    const a = auditSkeleton();
    return { ils: baseTotal, audit: a };
  }

  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const fieldName = persistedDisplay?.fieldName ?? null;

  const publicFinal = parsePositiveMoney(op.public_final_customer_price_ils);
  const displayTotalOnOrder = parsePositiveMoney(order.display_total_ils);

  const baggageFromOp = parsePositiveMoney(op.baggagePrice ?? op.baggage_price);
  const baggageFromUiRaw = baggageDetails?.selectedPaidTotal ?? 0;
  const baggageFromUiNum = Number(baggageFromUiRaw);
  const baggageUi =
    Number.isFinite(baggageFromUiNum) && baggageFromUiNum > 0 ? baggageFromUiNum : 0;
  const baggageTotalPre = Math.max(
    baggageUi,
    baggageFromOp != null && baggageFromOp > 0 ? baggageFromOp : 0,
    parsePositiveMoney(op.public_baggage_total_ils) ?? 0
  );

  if (baseTotal == null || !Number.isFinite(baseTotal)) {
    const hw = Math.max(
      Number(publicFinal) || 0,
      Number(displayTotalOnOrder) || 0,
      baggageTotalPre
    );
    if (hw > 0) {
      const auditMissing = {
        baseTotal: null,
        baggageTotal: baggageTotalPre,
        supplementsTotal,
        displayedTotal: null,
        payableTotal: round2(hw),
        source: "success_missing_base_highwater",
        publicFinal: publicFinal ?? null,
        displayTotalOnOrder: displayTotalOnOrder ?? null,
      };
      if (typeof console !== "undefined" && console.log) {
        console.log("[PUBLIC_SUCCESS_TOTAL_AUDIT]", auditMissing);
      }
      return {
        ils: round2(hw),
        audit: auditMissing,
      };
    }
    const a = auditSkeleton();
    a.baggageTotal = baggageTotalPre;
    if (typeof console !== "undefined" && console.log) {
      console.log("[PUBLIC_SUCCESS_TOTAL_AUDIT]", a);
    }
    return { ils: baseTotal, audit: a };
  }

  /** שלב גבוה — מלאים ששרת הציב לעיתים מתחת ל־priceWithCC */
  let payableTotal = baseTotal;

  let elevationSource =
    persistedDisplay?.source &&
    persistedDisplay?.fieldName &&
    `${persistedDisplay.source}:${persistedDisplay.fieldName}`;

  if (Number.isFinite(publicFinal) && publicFinal > payableTotal + 0.005) {
    payableTotal = publicFinal;
    elevationSource = "public_final_customer_price_ils";
  }
  if (Number.isFinite(displayTotalOnOrder) && displayTotalOnOrder > payableTotal + 0.005) {
    payableTotal = displayTotalOnOrder;
    elevationSource = "display_total_ils";
  }

  const combinedBaseBaggage =
    baggageTotalPre > 0 ? round2(baseTotal + baggageTotalPre) : baseTotal;

  const canAdditiveBaggage =
    baggageTotalPre > 0 &&
    fieldName !== "sumpaid" &&
    fieldName !== "public_final_customer_price_ils" &&
    fieldName !== "display_total_ils" &&
    ADDITIVE_BAGGAGE_WHEN_MISSING_FULL_TOTAL.has(fieldName) &&
    combinedBaseBaggage > payableTotal + MONEY_TOLERANCE &&
    payableTotal <= baseTotal + MONEY_TOLERANCE;

  if (canAdditiveBaggage) {
    payableTotal = combinedBaseBaggage;
    elevationSource = `${elevationSource ?? "persisted"}+additive_baggage_line`;
  }

  const finalAudit = {
    baseTotal,
    baggageTotal: baggageTotalPre,
    supplementsTotal,
    displayedTotal: baseTotal,
    payableTotal: round2(payableTotal),
    source: elevationSource ?? persistedDisplay?.fieldName ?? "persisted",
    publicFinal: publicFinal ?? null,
    displayTotalOnOrder: displayTotalOnOrder ?? null,
    appliedAdditiveBaggage: canAdditiveBaggage,
  };

  if (typeof console !== "undefined" && console.log) {
    console.log("[PUBLIC_SUCCESS_TOTAL_AUDIT]", finalAudit);
  }

  return { ils: round2(payableTotal), audit: finalAudit };
}
